import React, { useMemo, useState } from "react";
import { Box, ButtonBase, Typography } from "@material-ui/core";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";

const isPlainObject = (value) =>
  value != null && typeof value === "object" && !Array.isArray(value);

const formatPrimitiveValue = (value) => {
  if (value == null || value === "") return "—";
  if (typeof value === "boolean" || typeof value === "number") return String(value);
  if (typeof value === "string") return value;
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

/** Short one-line summary for collapsed object/array nodes. */
export const summarizeValue = (value) => {
  if (value == null) return "empty";
  if (Array.isArray(value)) {
    if (value.length === 0) return "empty list";
    const allPrimitive = value.every((item) => item == null || typeof item !== "object");
    if (allPrimitive) {
      const preview = value
        .slice(0, 3)
        .map((item) => formatPrimitiveValue(item))
        .join(", ");
      return value.length <= 3 ? preview : `${preview}… (${value.length})`;
    }
    return `${value.length} item${value.length === 1 ? "" : "s"}`;
  }
  if (isPlainObject(value)) {
    const keys = Object.keys(value);
    if (keys.length === 0) return "empty object";
    if (keys.includes("op")) {
      const op = value.op || value.action || "?";
      const extra =
        value.url || value.selector || value.text || value.goal || value.milliseconds || "";
      const extraStr = extra ? ` · ${String(extra).slice(0, 48)}${String(extra).length > 48 ? "…" : ""}` : "";
      return `${op}${extraStr}`;
    }
    if (keys.includes("goal") && keys.includes("actions") && Array.isArray(value.actions)) {
      const goal = String(value.goal || "").slice(0, 56);
      return `${value.actions.length} step${value.actions.length === 1 ? "" : "s"}${
        goal ? ` · ${goal}${String(value.goal).length > 56 ? "…" : ""}` : ""
      }`;
    }
    if (keys.length <= 3) return keys.join(", ");
    return `${keys.length} fields (${keys.slice(0, 2).join(", ")}…)`;
  }
  return formatPrimitiveValue(value);
};

const ToggleRow = ({ open, onToggle, label, summary, depth }) => (
  <ButtonBase
    onClick={onToggle}
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      width: "100%",
      textAlign: "left",
      padding: "2px 0",
      borderRadius: 4,
      gap: 2,
    }}
  >
    {open ? (
      <ExpandMoreIcon style={{ fontSize: 18, opacity: 0.7, flexShrink: 0 }} />
    ) : (
      <ChevronRightIcon style={{ fontSize: 18, opacity: 0.7, flexShrink: 0 }} />
    )}
    <Typography
      variant="body2"
      component="span"
      style={{
        fontWeight: 600,
        wordBreak: "break-word",
        marginRight: 6,
      }}
    >
      {label}
    </Typography>
    {!open ? (
      <Typography
        variant="body2"
        component="span"
        color="textSecondary"
        style={{
          fontWeight: 400,
          wordBreak: "break-word",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minWidth: 0,
          flex: 1,
        }}
      >
        {summary}
      </Typography>
    ) : null}
  </ButtonBase>
);

/**
 * Nested object/array viewer with collapse/expand (defaults collapsed past depth 0).
 */
export const CollapsibleValueView = ({
  value,
  depth = 0,
  label = null,
  /** When true, this node starts expanded. Root payload usually expanded; nested collapsed. */
  defaultOpen = null,
}) => {
  const complex = isPlainObject(value) || Array.isArray(value);
  const startOpen = defaultOpen != null ? defaultOpen : depth === 0;
  const [open, setOpen] = useState(startOpen);

  const summary = useMemo(() => summarizeValue(value), [value]);

  if (!complex) {
    if (label != null) {
      return (
        <Box style={{ marginTop: depth === 0 ? 0 : 4, width: "100%" }}>
          <Typography variant="body2" component="div" style={{ wordBreak: "break-word" }}>
            <span style={{ fontWeight: 600 }}>{label}</span>
            <span style={{ fontWeight: 400, marginLeft: 6 }}>{formatPrimitiveValue(value)}</span>
          </Typography>
        </Box>
      );
    }
    return (
      <Typography variant="body2" component="span" style={{ wordBreak: "break-word" }}>
        {formatPrimitiveValue(value)}
      </Typography>
    );
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return label != null ? (
        <Typography variant="body2">
          <span style={{ fontWeight: 600 }}>{label}</span>
          <span style={{ marginLeft: 6, opacity: 0.7 }}>[]</span>
        </Typography>
      ) : (
        <Typography variant="body2" color="textSecondary">
          []
        </Typography>
      );
    }
    const allPrimitive = value.every((item) => item == null || typeof item !== "object");
    if (allPrimitive && !label) {
      return (
        <Typography variant="body2" component="span" style={{ wordBreak: "break-word" }}>
          {value.map((item) => formatPrimitiveValue(item)).join(", ")}
        </Typography>
      );
    }

    return (
      <Box style={{ width: "100%", marginTop: depth > 0 ? 4 : 0 }}>
        {label != null ? (
          <ToggleRow
            open={open}
            onToggle={() => setOpen((v) => !v)}
            label={label}
            summary={summary}
            depth={depth}
          />
        ) : null}
        {(open || label == null) && (
          <Box
            style={{
              width: "100%",
              paddingLeft: label != null ? 10 : 0,
              borderLeft: label != null ? "2px solid rgba(0,0,0,0.08)" : undefined,
            }}
          >
            {value.map((item, index) => {
              const itemComplex = isPlainObject(item) || Array.isArray(item);
              if (!itemComplex) {
                return (
                  <Typography
                    key={index}
                    variant="body2"
                    style={{ marginTop: 4, wordBreak: "break-word" }}
                  >
                    <span style={{ opacity: 0.65 }}>[{index}]</span>{" "}
                    {formatPrimitiveValue(item)}
                  </Typography>
                );
              }
              return (
                <CollapsibleValueView
                  key={index}
                  value={item}
                  depth={depth + 1}
                  label={`[${index}]`}
                  defaultOpen={false}
                />
              );
            })}
          </Box>
        )}
      </Box>
    );
  }

  // object
  const entries = Object.entries(value);
  if (entries.length === 0) {
    return label != null ? (
      <Typography variant="body2">
        <span style={{ fontWeight: 600 }}>{label}</span>
        <span style={{ marginLeft: 6, opacity: 0.7 }}>{"{}"}</span>
      </Typography>
    ) : (
      <Typography variant="body2" color="textSecondary">
        {"{}"}
      </Typography>
    );
  }

  return (
    <Box style={{ width: "100%", marginTop: depth > 0 ? 4 : 0 }}>
      {label != null ? (
        <ToggleRow
          open={open}
          onToggle={() => setOpen((v) => !v)}
          label={label}
          summary={summary}
          depth={depth}
        />
      ) : null}
      {(open || label == null) && (
        <Box
          style={{
            width: "100%",
            paddingLeft: label != null ? 10 : 0,
            borderLeft: label != null ? "2px solid rgba(0,0,0,0.08)" : undefined,
          }}
        >
          {entries.map(([key, nested]) => {
            const nestedComplex = isPlainObject(nested) || Array.isArray(nested);
            if (!nestedComplex) {
              return (
                <Typography
                  key={key}
                  variant="body2"
                  style={{ marginTop: 6, wordBreak: "break-word" }}
                >
                  <span style={{ fontWeight: 600 }}>{key}</span>
                  <span style={{ fontWeight: 400, marginLeft: 6 }}>
                    {formatPrimitiveValue(nested)}
                  </span>
                </Typography>
              );
            }
            return (
              <CollapsibleValueView
                key={key}
                value={nested}
                depth={depth + 1}
                label={key}
                defaultOpen={false}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default CollapsibleValueView;
